import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  CircularProgress,
  Link,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  FormControl,
  Select,
  Tooltip,
  Button,
  Popper,
  Grow,
  Paper,
  ClickAwayListener,
  Collapse,
  useMediaQuery,
  Stack,
  useTheme,
  Divider,
} from '@mui/material';
import { Logout, ExpandMore, Login, ConfirmationNumberOutlined } from '@mui/icons-material';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useNavigate, useLocation, Link as RouterLink } from 'react-router-dom';
import Alltime from '../Pages/Alltime';
import AlltimeTable from '../Pages/AlltimeTable';
import AlltimeGames from '../Pages/AlltimeGames';
import SeasonGames from '../Pages/SeasonGames';
import Matchup from '../Pages/Matchup';
import MyTeam from '../Pages/MyTeam';
import MatchupProjection from '../Pages/MatchupProjection';
import Rankings from '../Pages/Rankings';
import FantasyChat from '../Pages/FantasyChat';
import LeaguePlayoffs from '../Pages/LeaguePlayoffs';
import NBAPlayoffs from '../Pages/NBAPlayoffs';
import MyLeaguePlayoffs from '../Pages/MyLeaguePlayoffs';
import NBARegularSeason from '../Pages/NBARegularSeason';
import MyLeagueRegularSeason from '../Pages/MyLeagueRegularSeason';
import UltimateWinner from '../Pages/UltimateWinner';
import CategoryBreakdown from '../Pages/CategoryBreakdown';
import About from '../Pages/About';
import UserProfile from '../Pages/UserProfile';
import PrivacyPolicy from '../Pages/PrivacyPolicy';
import Terms from '../Pages/Terms';
import Posts from '../Pages/Posts';
import Post from '../Pages/Post';
import Guides from '../Pages/Guides';
import Guide from '../Pages/Guide';
import Pricing from '../Pages/Pricing';
import PurchaseSuccess from '../Pages/PurchaseSuccess';
import GatedTool from './GatedTool';
import { useAuth } from '../contexts/AuthContext';
import { useEntitlements } from '../hooks/useEntitlements';
import { LeagueProvider } from '../contexts/LeagueContext';
import { supabase } from '../utils/supabase';
import ReassuringLoader from './ReassuringLoader';
import SEOContent from './SEOContent';
import AdSlot from './AdSlot';
import { AD_SLOTS, DISPLAY_AD_PATHS } from '../config/ads';
import AuthModal from './AuthModal';
import { YAHOO_ENABLED } from '../config/yahoo';

// While Yahoo is paused, the nav tooltips on Yahoo tools say so instead of
// inviting a connect that would fail.
const yahooTip = (text) => (YAHOO_ENABLED ? text : 'Yahoo league sync is temporarily unavailable');

// === ICON WRAPPER FOR RESPONSIVE SIZING ===
const IconWrapper = ({ children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const size = isMobile ? 26 : 28;
  return React.cloneElement(children, {
    style: { width: size, height: size, ...children.props.style },
  });
};
const MyTeamIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 2L15.09 8.26L22 9L17 14L18.18 21L12 17.77L5.82 21L7 14L2 9L8.91 8.26L12 2Z" />
  </svg>
);

// === SUBMENU ICONS (unchanged - keep your favorites) ===
const MatchupIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const RankingsIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const AIIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
    <circle cx="9" cy="10" r="1" />
    <circle cx="15" cy="10" r="1" />
    <path d="M9 15c.5.8 1.5 1.5 3 1.5s2.5-.7 3-1.5" />
  </svg>
);

const PlayoffIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
  </svg>
);

const HistoryIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <polyline points="12 6 12 12 16 14" />
  </svg>
);

const AboutIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="12" cy="12" r="10" />
    <line x1="12" y1="16" x2="12" y2="12" />
    <line x1="12" y1="8" x2="12.01" y2="8" />
  </svg>
);

const TeamsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
    <path d="M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

const SeasonsIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="16" rx="2" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <path d="M12 14l-1 3h2l-1-3z" />
  </svg>
);

const GamesIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16v16H4z" />
    <path d="M8 12l4-4 4 4" />
    <circle cx="12" cy="15" r="2" />
    <path d="M4 8l8-4 8 4" />
  </svg>
);



const ProjectionIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M7 16l4-4 4 4 6-6" />
    <circle cx="7" cy="16" r="2" />
    <circle cx="11" cy="12" r="2" />
    <circle cx="15" cy="16" r="2" />
    <circle cx="21" cy="10" r="2" />
  </svg>
);

const SeasonGamesIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <path d="M3 9h18" />
    <path d="M9 21V9" />
    <circle cx="15" cy="15" r="3" />
    <path d="M15 12v3l2 1" />
  </svg>
);

const ScheduleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);

const UltimateWinnerIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="9" y1="21" x2="9" y2="9" />
    <path d="M12 5l1.5 3 3.5 0.5-2.5 2.5 0.5 3.5-3-1.5-3 1.5 0.5-3.5-2.5-2.5 3.5-0.5z" />
  </svg>
);

const CategoryBreakdownIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="3" y1="15" x2="21" y2="15" />
    <line x1="9" y1="9" x2="9" y2="21" />
    <line x1="15" y1="9" x2="15" y2="21" />
  </svg>
);

const AlltimeLayout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout, login, authUser, isSignedIn, signOutAccount } = useAuth();
  const { hasPass, hasAnyPass } = useEntitlements();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Show the full wordmark only when there's comfortable room; between md and lg
  // the desktop nav keeps all items but drops the wordmark text to avoid clipping.
  // A truly centred nav splits the leftover space evenly, so the brand only has
  // half of it. Goat + wordmark needs ~357px; that much is only available from
  // ~1440 up. Below it the goat carries the brand and the nav stays centred.
  const isLarge = useMediaQuery('(min-width:1440px)');

  const [isPageLoading, setIsPageLoading] = useState(false);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const [mobileExpandedMenu, setMobileExpandedMenu] = useState(null);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [leagueAnchorEl, setLeagueAnchorEl] = useState(null);
  const [rankingsAnchorEl, setRankingsAnchorEl] = useState(null);
  const [scheduleAnchorEl, setScheduleAnchorEl] = useState(null);
  const [alltimeAnchorEl, setAlltimeAnchorEl] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [userLeagues, setUserLeagues] = useState([]);
  const [selectedLeague, setSelectedLeague] = useState(null);
  const [leagueTeams, setLeagueTeams] = useState([]);
  const [currentMatchup, setCurrentMatchup] = useState(null);
  const [yahooConnecting, setYahooConnecting] = useState(false);
  const [loadingLeagues, setLoadingLeagues] = useState(false);
  // The league whose teams fetch has FINISHED — note "finished", not "returned
  // something". A user with no Yahoo league for the upcoming season settles with
  // zero teams, and pages that wait on league data must be able to tell that
  // apart from "still loading". Keyed by league id so switching leagues reads as
  // loading immediately, without a frame of stale "settled".
  const [teamsSettledFor, setTeamsSettledFor] = useState(null);
  const [leagueSettings, setLeagueSettings] = useState(null);
  const [authModalOpen, setAuthModalOpen] = useState(false);

  // === MAIN NAV ITEMS WITH NEW SVGs ===
  const navItems = [
    {
      path: '/league',
      label: 'League',
      icon: (
        <img
          src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/menu/trophy.svg"
          alt="League"
        />
      ),
      hasSubmenu: true,
    },
    {
      path: '/rankings',
      label: 'Rankings',
      icon: (
        <img
          src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/menu/rankings.svg"
          alt="Rankings"
        />
      ),
      hasSubmenu: true,
    },
    {
      path: '/guides',
      label: 'Guides',
      icon: (
        <img
          src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/menu/blog.svg"
          alt="Guides"
        />
      ),
      requiresAuth: false,
      requiresPremium: false,
    },
    {
      path: '/schedule',
      label: 'Schedule',
      icon: (
        <img
          src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/menu/calendar.svg"
          alt="Schedule"
        />
      ),
      hasSubmenu: true,
    },
    {
      path: '/alltime',
      label: 'Alltime',
      icon: (
        <img
          src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/menu/wilt.svg"
          alt="Alltime"
        />
      ),
      hasSubmenu: true,
    },
  ];

  const leagueSubmenu = [
    { path: '/matchup', label: 'Matchup', icon: <ProjectionIcon />, requiresAuth: false },
    { path: '/my-team', label: 'My Team', icon: <MyTeamIcon />, requiresAuth: false },
    {
      path: '/matchup-projection',
      label: 'Matchup Projection',
      icon: <MatchupIcon />,
      requiresAuth: true,
      requiresPremium: true,
      tooltip: yahooTip('Connect to Yahoo to view your matchups'),
      premiumTooltip: 'Requires a Season Pass'
    },
    {
      path: '/ultimate-winner',
      label: 'Head-to-Head Matrix',
      icon: <UltimateWinnerIcon />,
      requiresAuth: true,
      requiresPremium: true,
      tooltip: yahooTip('Connect to Yahoo to view head-to-head matrix'),
      premiumTooltip: 'Requires a Season Pass'
    },
    {
      path: '/category-breakdown',
      label: 'Category Breakdown',
      icon: <CategoryBreakdownIcon />,
      requiresAuth: true,
      requiresPremium: true,
      tooltip: yahooTip('Connect to Yahoo to view category breakdown'),
      premiumTooltip: 'Requires a Season Pass'
    },
  ];

  const rankingsSubmenu = [
    { path: '/rankings', label: 'Players', icon: <GamesIcon />, requiresAuth: false },
    { path: '/season-games', label: 'Top Games', icon: <SeasonGamesIcon />, requiresAuth: false },

  ];

  const scheduleSubmenu = [
    { path: '/nba-playoffs', label: 'Fantasy Playoffs', icon: <PlayoffIcon />, requiresAuth: false },
    { path: '/nba-regular-season', label: 'Fantasy Season', icon: <ScheduleIcon />, requiresAuth: false },
    {
      path: '/my-league-playoffs',
      label: 'Team Playoff Strength',
      icon: <MyTeamIcon />,
      requiresAuth: true,
      requiresPremium: true,
      tooltip: yahooTip('Connect to Yahoo to view your playoff schedule'),
      premiumTooltip: 'Requires a Season Pass'
    },
    {
      path: '/my-league-regular-season',
      label: 'Team Season Strength',
      icon: <MyTeamIcon />,
      requiresAuth: true,
      requiresPremium: true,
      tooltip: yahooTip('Connect to Yahoo to view your league schedule'),
      premiumTooltip: 'Requires a Season Pass'
    },
  ];

  const alltimeSubmenu = [
    { path: '/games', label: 'Alltime Games', icon: <GamesIcon />, requiresAuth: false },
    { path: '/seasons', label: 'Alltime Seasons', icon: <SeasonsIcon />, requiresAuth: false },
    { path: '/teams', label: 'Alltime Teams', icon: <TeamsIcon />, requiresAuth: false },

  ];


  // === DATA FETCHING ===
  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.userId) return;
      try {
        const { data, error } = await supabase
          .from('user_profiles')
          .select('name, email, send_weekly_projections, send_news, is_premium')
          .eq('user_id', user.userId)
          .maybeSingle();

        if (error) {
          console.error('Error fetching user profile:', error);
          return;
        }

        if (data) {
          console.log('User profile fetched successfully:', data);
          setUserProfile(data);
        } else {
          console.log('No user profile found for userId:', user.userId);
        }
      } catch (err) {
        console.error('Exception fetching user profile:', err);
      }
    };
    fetchUserProfile();
  }, [user?.userId]);

  // Yahoo league data still in flight. False as soon as it settles — including
  // when it settles empty (no leagues yet for the new season, or a league with
  // no teams), which is what stops a consumer from spinning forever.
  const isLoadingLeagueData =
    isAuthenticated && (loadingLeagues || (!!selectedLeague && teamsSettledFor !== selectedLeague));

  const displayName = userProfile?.name || user?.name || 'User';
  const displayPicture = userProfile?.profile_picture || user?.profilePicture;
  const isPremium = hasPass('season');
  const showDisplayAd = DISPLAY_AD_PATHS.some((path) =>
    path.endsWith('*')
      ? location.pathname.startsWith(path.slice(0, -1))
      : location.pathname === path
  );

  useEffect(() => {
    const fetchUserLeagues = async () => {
      if (!isAuthenticated || !user?.userId) return;
      setLoadingLeagues(true);
      try {
        const { data } = await supabase.functions.invoke('yahoo-fantasy-api', {
          body: { action: 'getUserLeagues', userId: user.userId },
        });
        if (data?.leagues?.length > 0) {
          setUserLeagues(data.leagues);
          if (!selectedLeague) {
            const firstLeagueId = String(data.leagues[0].leagueId);
            setSelectedLeague(firstLeagueId);
          }
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoadingLeagues(false);
      }
    };
    fetchUserLeagues();
  }, [user?.userId, isAuthenticated, selectedLeague]);

  useEffect(() => {
    const fetchLeagueTeams = async () => {
      if (!selectedLeague || !isAuthenticated) return;
      try {
        const { data } = await supabase.functions.invoke('yahoo-fantasy-api', {
          body: { action: 'getAllTeamsInLeague', userId: user.userId, leagueId: selectedLeague },
        });
        setLeagueTeams(data?.teams || []);
      } catch (err) {
        console.error('Error fetching league teams:', err);
        setLeagueTeams([]);
      } finally {
        setTeamsSettledFor(selectedLeague);
      }
    };
    fetchLeagueTeams();
  }, [selectedLeague, user?.userId, isAuthenticated]);

  useEffect(() => {
    const fetchLeagueSettings = async () => {
      if (!selectedLeague || !isAuthenticated || !user?.userId) {
        setLeagueSettings(null);
        return;
      }

      try {
        const { data } = await supabase.functions.invoke('yahoo-fantasy-api', {
          body: { action: 'getLeagueSettings', userId: user.userId, leagueId: selectedLeague },
        });

        if (data) {
          setLeagueSettings(data);
        }
      } catch (err) {
        console.error('Error fetching league settings:', err);
        setLeagueSettings(null);
      }
    };
    fetchLeagueSettings();
  }, [selectedLeague, user?.userId, isAuthenticated]);

  useEffect(() => {
    const fetchCurrentMatchup = async () => {
      if (!selectedLeague || !isAuthenticated || !user?.userId) return;
      try {
        const { data, error } = await supabase.functions.invoke('yahoo-fantasy-api', {
          body: {
            action: 'getCurrentMatchup',
            userId: user.userId,
            leagueId: selectedLeague
          },
        });
        if (error) {
          console.error('Error fetching matchup:', error);
          setCurrentMatchup(null);
          return;
        }
        if (data?.matchup) {
          setCurrentMatchup(data.matchup);
        } else {
          setCurrentMatchup(null);
        }
      } catch (err) {
        console.error('Error fetching current matchup:', err);
        setCurrentMatchup(null);
      }
    };
    fetchCurrentMatchup();
  }, [selectedLeague, user?.userId, isAuthenticated]);

  const userTeamPlayers = useMemo(() => {
    if (!leagueTeams.length) return [];
    const userTeam = leagueTeams.find(t => t.is_owned_by_current_login);
    return userTeam?.players?.map(p => ({ nbaPlayerId: p.nbaPlayerId, yahooPlayerId: p.yahooPlayerId, name: p.name })) || [];
  }, [leagueTeams]);

  const handleNavClick = (path) => {
    setIsPageLoading(true);
    navigate(path);
    setMobileMenuAnchor(null);
    setTimeout(() => setIsPageLoading(false), 300);
  };

  const handleYahooConnect = async () => {
    setYahooConnecting(true);
    setIsPageLoading(true);
    try {
      const currentPath = location.pathname;
      sessionStorage.setItem('oauth_return_path', currentPath);

      const isDev = window.location.hostname === 'localhost';
      const { data } = await supabase.functions.invoke('yahoo-oauth', { body: { action: 'authorize', isDev } });
      if (data?.authUrl) {
        setTimeout(() => {
          window.location.href = data.authUrl;
        }, 100);
      }
    } catch (err) {
      console.error(err);
      setYahooConnecting(false);
      setIsPageLoading(false);
    }
  };

  // Global OAuth callback handler
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get("code");

    if (code && !isAuthenticated && YAHOO_ENABLED) {
      const returnPath = sessionStorage.getItem('oauth_return_path') || '/matchup';
      sessionStorage.removeItem('oauth_return_path');

      window.history.replaceState({}, document.title, returnPath);

      const processCallback = async () => {
        setIsPageLoading(true);
        try {
          const isDev = window.location.hostname === 'localhost';
          const { data } = await supabase.functions.invoke('yahoo-oauth', {
            body: { action: 'callback', code, isDev }
          });

          if (data?.success) {
            login({
              userId: data.userId,
              email: data.email,
              name: data.name,
              profilePicture: data.profilePicture,
              expiresAt: data.expiresAt,
            });

            try {
              const { data: existingProfile, error: profileError } = await supabase
                .from('user_profiles')
                .select('user_id, name, email')
                .eq('user_id', data.userId)
                .single();

              if (profileError && profileError.code !== 'PGRST116') {
                throw profileError;
              }

              if (existingProfile && !profileError) {
                const updateData = {};
                if (!existingProfile.name && data.name) updateData.name = data.name;
                if (!existingProfile.email && data.email) updateData.email = data.email;
                if (Object.keys(updateData).length > 0) {
                  await supabase.from('user_profiles').update(updateData).eq('user_id', data.userId);
                }
              } else {
                await supabase.from('user_profiles').insert({
                  user_id: data.userId,
                  name: data.name || '',
                  email: data.email || '',
                  send_weekly_projections: true,
                  send_news: true,
                });
              }
            } catch (profileErr) {
              console.error('Error updating user profile:', profileErr);
            }

            navigate(returnPath);
          } else {
            setIsPageLoading(false);
          }
        } catch (err) {
          console.error('OAuth callback error:', err);
          setIsPageLoading(false);
        }
      };

      processCallback();
    }
  }, [isAuthenticated, navigate, login]);

  const closeAllMenus = () => {
    setLeagueAnchorEl(null);
    setRankingsAnchorEl(null);
    setScheduleAnchorEl(null);
    setAlltimeAnchorEl(null);
  };

  // Hover-intent: opening on hover needs a short close delay so the cursor can
  // travel the gap from the trigger to the panel without the menu snapping shut.
  // Entering the panel cancels the pending close; leaving either re-arms it.
  const closeTimer = useRef(null);
  const cancelClose = () => {
    if (closeTimer.current) {
      clearTimeout(closeTimer.current);
      closeTimer.current = null;
    }
  };
  const scheduleClose = () => {
    cancelClose();
    closeTimer.current = setTimeout(closeAllMenus, 140);
  };
  // Open one menu on hover, keeping it stable if it's already the open one.
  const openMenu = (setAnchor, current) => (e) => {
    if (isMobile) return;
    cancelClose();
    if (current === e.currentTarget) return;
    closeAllMenus();
    setAnchor(e.currentTarget);
  };

  // Clicking a parent goes straight to its first submenu item; hover still
  // opens the panel to reach the others. (Desktop only — the mobile drawer
  // expands sections instead, or its other items would be unreachable.)
  const goToFirstItem = (submenu) => (e) => {
    e.preventDefault();
    cancelClose();
    closeAllMenus();
    navigate(submenu[0].path);
  };

  const handleLeagueOpen = openMenu(setLeagueAnchorEl, leagueAnchorEl);
  const handleRankingsOpen = openMenu(setRankingsAnchorEl, rankingsAnchorEl);
  const handleScheduleOpen = openMenu(setScheduleAnchorEl, scheduleAnchorEl);
  const handleAlltimeOpen = openMenu(setAlltimeAnchorEl, alltimeAnchorEl);

  const renderContent = () => {
    const p = location.pathname;
    if (p === '/' || p === '/matchup') return <Matchup />;
    if (p === '/matchup-projection') return <GatedTool toolId="matchup-projection" toolName="Matchup Projection"><MatchupProjection /></GatedTool>;
    if (p === '/my-team') return <MyTeam />;
    if (p === '/ultimate-winner') return <GatedTool toolId="ultimate-winner" toolName="the Head-to-Head Matrix"><UltimateWinner /></GatedTool>;
    if (p === '/category-breakdown') return <GatedTool toolId="category-breakdown" toolName="Category Breakdown"><CategoryBreakdown /></GatedTool>;
    if (p === '/teams') return <Alltime />;
    if (p === '/seasons' || p === '/table') return <AlltimeTable />;
    if (p === '/games') return <AlltimeGames />;
    if (p === '/season-games') return <SeasonGames />;
    if (p === '/rankings') return <Rankings />;
    if (p === '/chat') return <FantasyChat />;
    if (p === '/playoffs') return <LeaguePlayoffs />;
    if (p === '/nba-playoffs') return <NBAPlayoffs />;
    if (p === '/my-league-playoffs') return <GatedTool toolId="my-league-playoffs" toolName="Team Playoff Strength"><MyLeaguePlayoffs /></GatedTool>;
    if (p === '/nba-regular-season') return <NBARegularSeason />;
    if (p === '/my-league-regular-season') return <GatedTool toolId="my-league-regular-season" toolName="Team Season Strength"><MyLeagueRegularSeason /></GatedTool>;
    if (p === '/about') return <About />;
    if (p === '/profile') return <UserProfile />;
    if (p === '/privacy-policy') return <PrivacyPolicy />;
    if (p === '/terms') return <Terms />;
    if (p === '/posts') return <Posts />;
    if (p.startsWith('/post/')) return <Post />;
    if (p === '/guides' || p === '/guides/') return <Guides />;
    if (p.startsWith("/guides/")) return <Guide onRequireSignIn={() => setAuthModalOpen(true)} />;
    if (p === '/pricing') return <Pricing onRequireSignIn={() => setAuthModalOpen(true)} />;
    if (p === '/purchase-success') return <PurchaseSuccess />;
    return <Matchup />;
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', bgcolor: '#EEEEEE' }}>
      {/* HEADER */}
      <Box sx={{
        background: '#ffffff',
        borderBottom: '1px solid #e2e6ec',
        boxShadow: '0 1px 3px rgba(15,35,64,0.06)',
        position: 'relative',
        zIndex: 1300,
      }}>
        <Box sx={{
          display: { xs: 'flex', md: 'grid' },
          gridTemplateColumns: { md: '1fr auto 1fr' },
          alignItems: 'center',
          justifyContent: { xs: 'space-between', md: 'initial' },
          px: { xs: 2, sm: 3 },
          py: 2,
          minHeight: 76,
        }}>
          {/* LEFT: brand, plus an explicit menu button on mobile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 0.25 : 2, justifySelf: 'start', minWidth: 0 }}>
            <Avatar
              src="https://fqrnmcnvrrujiutstkgb.supabase.co/storage/v1/object/public/avatars/goat_1.svg"
              alt="GOAT"
              role="button"
              aria-label={isMobile ? 'Open menu' : 'Fantasy Goats Guru home'}
              aria-haspopup={isMobile ? 'true' : undefined}
              aria-expanded={isMobile ? Boolean(mobileMenuAnchor) : undefined}
              onClick={(e) => isMobile ? setMobileMenuAnchor(e.currentTarget) : navigate('/guides')}
              sx={{
                width: { xs: 50, sm: 56 },
                height: { xs: 50, sm: 56 },
                border: '4px solid #4a90e2',
                cursor: 'pointer',
                transition: 'transform 0.2s',
                '&:active': { transform: 'scale(0.95)' },
              }}
            />
            {isMobile && (
              <ArrowDropDownIcon
                onClick={(e) => setMobileMenuAnchor(e.currentTarget)}
                sx={{ color: '#9aa4b2', ml: -0.25, cursor: 'pointer' }}
              />
            )}

            {isLarge && (
              <Tooltip title="Fantasy Goats Guru">
                <Typography
                  variant="h5"
                  noWrap
                  sx={{
                    fontSize: { sm: '1.75rem', md: '1.9rem' },
                    fontWeight: 800,
                    background: 'linear-gradient(90deg, #4A70A9 0%, #6a89cc 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Fantasy Goats Guru
                </Typography>
              </Tooltip>
            )}
          </Box>

          {/* DESKTOP NAV */}
          {!isMobile && (
            <Box sx={{ display: 'flex', gap: 0.75, justifySelf: 'center' }}>
              {navItems.map((item) => {
                const submenuMap = {
                  '/league': { submenu: leagueSubmenu, anchorEl: leagueAnchorEl, handleOpen: handleLeagueOpen, handleClose: scheduleClose, },
                  '/rankings': { submenu: rankingsSubmenu, anchorEl: rankingsAnchorEl, handleOpen: handleRankingsOpen, handleClose: scheduleClose, },
                  '/schedule': { submenu: scheduleSubmenu, anchorEl: scheduleAnchorEl, handleOpen: handleScheduleOpen, handleClose: scheduleClose, },
                  '/alltime': { submenu: alltimeSubmenu, anchorEl: alltimeAnchorEl, handleOpen: handleAlltimeOpen, handleClose: scheduleClose, },
                };

                const config = submenuMap[item.path];

                // FIXED LOGIC: Keep "Guides" active on the guides list OR a single guide page
                const isActive =
                  location.pathname === item.path ||
                  (item.path === '/guides' && location.pathname.startsWith('/guides'));

                const isSubmenuActive = config && config.submenu.some(s => s.path === location.pathname);

                return item.hasSubmenu ? (
                  <Box
                    key={item.path}
                    sx={{ position: 'relative' }}
                    onMouseEnter={config.handleOpen}
                    onMouseLeave={config.handleClose}
                  >
                    <Button
                      onMouseEnter={config.handleOpen}
                      onClick={goToFirstItem(config.submenu)}
                      onKeyDown={(e) => { if (e.key === 'Escape') closeAllMenus(); }}
                      aria-haspopup="true"
                      aria-expanded={Boolean(config.anchorEl)}
                      startIcon={<IconWrapper>{item.icon}</IconWrapper>}
                      endIcon={<ArrowDropDownIcon sx={{ ml: -0.5, color: '#9aa4b2' }} />}
                      sx={{
                        px: { xs: 1, md: 1.25 },
                        py: 1.5,
                        minWidth: 0,
                        fontSize: '0.92rem',
                        fontWeight: isSubmenuActive ? 700 : 600,
                        '& .MuiButton-startIcon': { mr: 0.75 },
                        '& .MuiButton-endIcon': { ml: 0 },
                        color: isSubmenuActive ? '#0f2340' : '#48505c',
                        borderRadius: 0,
                        textTransform: 'none',
                        borderBottom: isSubmenuActive ? '2px solid #4a90e2' : '2px solid transparent',
                        '&:hover': { bgcolor: 'transparent', color: '#0f2340', borderBottom: '2px solid #c9d3e0' },
                      }}
                    >
                      {item.label}
                    </Button>

                    <Popper
                      open={Boolean(config.anchorEl)}
                      anchorEl={config.anchorEl}
                      placement="bottom-start"
                      transition
                      sx={{ zIndex: 1400 }}
                      modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
                    >
                      {({ TransitionProps }) => (
                        <Grow {...TransitionProps}>
                          <Paper
                            elevation={20}
                            onMouseEnter={cancelClose}
                            onMouseLeave={config.handleClose}
                            sx={{
                                bgcolor: '#fff',
                              border: '1px solid #e2e6ec',
                              borderRadius: 1.5,
                              overflow: 'hidden',
                              minWidth: 232,
                              boxShadow: '0 10px 28px rgba(15,35,64,0.12)',
                            }}
                          >
                            <ClickAwayListener onClickAway={config.handleClose}>
                              <Box>
                                {config.submenu.map((sub, subIndex) => {
                                  // Free tools first, then a labelled group for
                                  // the pass-gated ones, so the menu reads as
                                  // "what I can use now" / "what a pass adds"
                                  // instead of a flat list dotted with badges.
                                  const prev = config.submenu[subIndex - 1];
                                  const startsLeagueGroup =
                                    sub.requiresAuth && (!prev || !prev.requiresAuth);
                                  // Both Yahoo- and premium-gated tools stay navigable — the
                                  // destination page renders the right gate (Connect Yahoo /
                                  // PremiumGate). Nav never blocks the click.
                                  const needsAuth = sub.requiresAuth && !isAuthenticated;
                                  const needsPremium = sub.requiresPremium && !isPremium;

                                  const tooltipContent = needsAuth && sub.tooltip
                                    ? sub.tooltip
                                    : needsPremium && sub.premiumTooltip
                                      ? sub.premiumTooltip
                                      : '';

                                  return (
                                    <React.Fragment key={sub.path}>
                                    {startsLeagueGroup && (
                                      <Box
                                        sx={{
                                          px: 2,
                                          pt: 1.25,
                                          pb: 0.5,
                                          mt: 0.5,
                                          borderTop: '1px solid #eef1f5',
                                          fontSize: '0.62rem',
                                          fontWeight: 700,
                                          letterSpacing: 1.2,
                                          textTransform: 'uppercase',
                                          color: '#9aa4b2',
                                        }}
                                      >
                                        Your Yahoo league
                                      </Box>
                                    )}
                                    <Tooltip
                                      title={tooltipContent}
                                      placement="right"
                                      arrow
                                    >
                                      <span>
                                        <MenuItem
                                          onClick={() => {
                                            handleNavClick(sub.path);
                                            config.handleClose();
                                          }}
                                          sx={{
                                            px: 3, py: 2,
                                            bgcolor: location.pathname === sub.path ? 'rgba(74,144,226,0.18)' : 'transparent',
                                            borderBottom: '1px solid #f0f0f0',
                                            '&:last-child': { borderBottom: 'none' },
                                            '&:hover': { bgcolor: 'rgba(74,144,226,0.12)' },
                                          }}
                                        >
                                          <Typography fontWeight={600} fontSize="0.95rem" sx={{ flexGrow: 1 }}>{sub.label}</Typography>
                                        </MenuItem>
                                      </span>
                                    </Tooltip>
                                    </React.Fragment>
                                  );
                                })}
                              </Box>
                            </ClickAwayListener>
                          </Paper>
                        </Grow>
                      )}
                    </Popper>
                  </Box>
                ) : (
                  <Tooltip
                    title={
                      item.requiresAuth && !isAuthenticated && item.tooltip
                        ? item.tooltip
                        : item.requiresPremium && !isPremium && item.premiumTooltip
                          ? (
                            <Box>
                              {item.premiumTooltip}{' '}
                              <Link
                                href="https://buymeacoffee.com/fantasygoatsguru"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}
                              >
                                Support us here
                              </Link>
                            </Box>
                          )
                          : ''
                    }
                    placement="bottom"
                    arrow
                  >
                    <span>
                      <Button
                        key={item.path}
                        onClick={() => {
                          const isDisabled = (item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium);
                          if (!isDisabled) {
                            handleNavClick(item.path);
                          }
                        }}
                        disabled={(item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium)}
                        startIcon={<IconWrapper>{item.icon}</IconWrapper>}
                        sx={{
                          px: { xs: 1, md: 1.25 },
                          py: 1.5,
                          minWidth: 0,
                          fontSize: '0.92rem',
                          fontWeight: isActive ? 700 : 600,
                          '& .MuiButton-startIcon': { mr: 0.75 },
                          color: isActive ? '#0f2340' : '#48505c',
                          borderRadius: 0,
                          textTransform: 'none',
                          borderBottom: isActive ? '2px solid #4a90e2' : '2px solid transparent',
                          '&:hover': { bgcolor: 'transparent', color: '#0f2340', borderBottom: '2px solid #c9d3e0' },
                          opacity: (item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium) ? 0.5 : 1,
                          cursor: (item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium) ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {item.label}
                      </Button>
                    </span>
                  </Tooltip>
                );
              })}
            </Box>
          )}

          {/* RIGHT: Passes + Account + Connect to Yahoo + Profile */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 }, flexShrink: 0, justifySelf: 'end' }}>
            {/* The passes had no entry point anywhere in the tools — six links
                existed, all buried in paywalls a free user rarely reaches. Hidden
                once you own one; the profile menu still links there. */}
            {!isMobile && !hasAnyPass && (
              <Button
                onClick={() => handleNavClick('/pricing')}
                sx={{
                  height: 38,
                  fontSize: '0.85rem',
                  fontWeight: 600,
                  color: location.pathname === '/pricing' ? '#0f2340' : '#48505c',
                  textTransform: 'none',
                  borderRadius: 0,
                  px: 1,
                  minWidth: 0,
                  borderBottom: location.pathname === '/pricing' ? '2px solid #4a90e2' : '2px solid transparent',
                  '&:hover': { bgcolor: 'transparent', color: '#0f2340', borderBottom: '2px solid #c9d3e0' },
                }}
              >
                Passes
              </Button>
            )}
            {/* Yahoo is a data-source integration, not a login, so connecting it
                must not require a Goats account first. The action previously
                lived only in the profile menu, which does not render until you
                are signed in or already connected — leaving a new visitor with
                no way in from the header. */}
            {!isAuthenticated && YAHOO_ENABLED && (
              <Tooltip title="Connect your Yahoo league — no account needed">
                <Button
                  variant="outlined"
                  onClick={handleYahooConnect}
                  disabled={yahooConnecting}
                  startIcon={yahooConnecting
                    ? <CircularProgress size={14} color="inherit" />
                    : <SportsBasketballIcon sx={{ fontSize: '1rem' }} />}
                  sx={{
                    height: 38,
                    fontSize: '0.8rem',
                    fontWeight: 600,
                    color: '#48505c',
                    borderColor: '#d7dde5',
                    textTransform: 'none',
                    borderRadius: 3,
                    whiteSpace: 'nowrap',
                    px: { xs: 1.25, lg: 2 },
                    '&:hover': { borderColor: '#4a90e2', color: '#0f2340', bgcolor: 'rgba(74,144,226,0.06)' },
                  }}
                >
                  {isLarge ? 'Connect Yahoo' : 'Yahoo'}
                </Button>
              </Tooltip>
            )}
            {!isSignedIn && (
              <Button
                variant="outlined"
                startIcon={<Login />}
                onClick={() => setAuthModalOpen(true)}
                sx={{
                  height: 38,
                  fontSize: '0.8rem',
                  fontWeight: 700,
                  color: '#4a90e2',
                  borderColor: '#4a90e2',
                  textTransform: 'none',
                  borderRadius: 3,
                  px: { xs: 1.5, sm: 2 },
                  '&:hover': { borderColor: '#357abd', bgcolor: 'rgba(74,144,226,0.08)' },
                }}
              >
                Sign In
              </Button>
            )}
            {isAuthenticated && userLeagues.length > 0 ? (
              <FormControl size="small" sx={{ minWidth: { xs: 100, sm: 130 }, maxWidth: { xs: 150, sm: 260, md: 320 } }}>
                <Select
                  value={selectedLeague || ''}
                  onChange={(e) => {
                    const newLeagueId = e.target.value;

                    if (!newLeagueId) {
                      console.warn('⚠️ No league ID received in onChange');
                      return;
                    }

                    const leagueIdStr = String(newLeagueId);
                    setSelectedLeague(leagueIdStr);
                  }}
                  displayEmpty
                  renderValue={(value) => {
                    if (!value) return '';
                    const valueStr = String(value);
                    const selected = userLeagues.find(l => String(l.leagueId) === valueStr);
                    if (!selected) return '';
                    // Full league name, then your team in that league. Long
                    // names ellipsize; the team name drops on phones.
                    return (
                      <Box component="span" sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        <Box component="span" sx={{ fontWeight: 600 }}>{selected.name}</Box>
                        {selected.teamName && (
                          <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' }, color: 'text.secondary' }}>
                            {' · '}{selected.teamName}
                          </Box>
                        )}
                      </Box>
                    );
                  }}
                  title={(() => {
                    const selected = userLeagues.find(l => String(l.leagueId) === String(selectedLeague));
                    return selected ? [selected.name, selected.teamName].filter(Boolean).join(' · ') : undefined;
                  })()}
                  sx={{
                    bgcolor: '#fff',
                    height: 36,
                    fontSize: '0.8rem',
                    '& .MuiSelect-select': { py: 1 },
                  }}
                >
                  {userLeagues.map((l) => {
                    const leagueIdStr = String(l.leagueId);
                    const isDisabled = false; // TODO: Remove this once we have premium features
                    const menuItem = (
                      <MenuItem
                        key={l.leagueId}
                        value={leagueIdStr}
                        disabled={isDisabled}
                        sx={{
                          fontSize: '0.85rem',
                          opacity: isDisabled ? 0.5 : 1,
                          display: 'block',
                          py: 1,
                        }}
                      >
                        <Box sx={{ fontWeight: 600, lineHeight: 1.3 }}>{l.name}</Box>
                        {l.teamName && (
                          <Box sx={{ fontSize: '0.75rem', color: 'text.secondary', lineHeight: 1.3 }}>
                            {l.teamName}
                          </Box>
                        )}
                      </MenuItem>
                    );

                    if (isDisabled) {
                      return (
                        <Tooltip
                          key={l.leagueId}
                          title={
                            <Box>
                              Consider supporting us for any amount to open premium features for the season.{' '}
                              <Link
                                href="https://buymeacoffee.com/fantasygoatsguru"
                                target="_blank"
                                rel="noopener noreferrer"
                                sx={{ color: '#fff', textDecoration: 'underline', fontWeight: 600 }}
                              >
                                Support us here
                              </Link>
                            </Box>
                          }
                          placement="right"
                          arrow
                          enterDelay={500}
                        >
                          <span style={{ display: 'block' }}>
                            {menuItem}
                          </span>
                        </Tooltip>
                      );
                    }

                    return menuItem;
                  })}
                </Select>
              </FormControl>
            ) : null}
            {/* Yahoo is no longer a header login. It's a per-tool integration:
                tools that need league data prompt to connect contextually, and
                the profile menu offers "Connect Yahoo league" for signed-in users. */}

            {(isSignedIn || (isAuthenticated && user)) && (
              <IconButton onClick={(e) => setProfileAnchorEl(e.currentTarget)}>
                <Avatar
                  src={displayPicture?.includes('default') ? 'https://www.svgrepo.com/show/513271/basketball.svg' : displayPicture}
                  alt={displayName}
                  sx={{ width: 38, height: 38, border: '2px solid #4a90e2' }}
                >
                  {(displayName || authUser?.email || 'U')[0].toUpperCase()}
                </Avatar>
              </IconButton>
            )}
          </Box>
        </Box>

        {/* MOBILE MENU */}
        <Menu
          anchorEl={mobileMenuAnchor}
          open={Boolean(mobileMenuAnchor)}
          onClose={() => {
            setMobileMenuAnchor(null);
            setMobileExpandedMenu(null);
          }}
          PaperProps={{ sx: { width: 280, mt: 1.5, maxHeight: '70vh' } }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        >
          {/* Account (Supabase) — independent of Yahoo */}
          {!isSignedIn ? (
            <MenuItem
              onClick={() => { setMobileMenuAnchor(null); setAuthModalOpen(true); }}
              sx={{ py: 2, px: 2.5, gap: 2.5, color: '#4a90e2', fontWeight: 700 }}
            >
              <Login />
              <Typography fontWeight={700} fontSize="0.95rem">Sign In</Typography>
            </MenuItem>
          ) : (
            <MenuItem disabled sx={{ py: 1.5, px: 2.5, opacity: 1 }}>
              <Box>
                <Typography fontWeight={700} fontSize="0.9rem">Signed in</Typography>
                {authUser?.email && (
                  <Typography variant="caption" color="text.secondary">{authUser.email}</Typography>
                )}
              </Box>
            </MenuItem>
          )}

          {!isAuthenticated && YAHOO_ENABLED && (
            <MenuItem
              onClick={() => { setMobileMenuAnchor(null); handleYahooConnect(); }}
              disabled={yahooConnecting}
              sx={{ py: 2, px: 2.5, gap: 2.5 }}
            >
              <SportsBasketballIcon sx={{ color: '#4a90e2' }} />
              <Typography fontWeight={600} fontSize="0.95rem">Connect Yahoo league</Typography>
            </MenuItem>
          )}

          {!hasAnyPass && (
            <MenuItem
              onClick={() => handleNavClick('/pricing')}
              sx={{
                py: 2,
                px: 2.5,
                gap: 2.5,
                bgcolor: location.pathname === '/pricing' ? 'rgba(74,144,226,0.10)' : 'transparent',
              }}
            >
              <ConfirmationNumberOutlined sx={{ color: '#4a90e2' }} />
              <Typography fontWeight={600} fontSize="0.95rem">Passes</Typography>
            </MenuItem>
          )}
          <Divider />
          {navItems.map((item) => {
            const submenuMap = {
              '/league': leagueSubmenu,
              '/rankings': rankingsSubmenu,
              '/schedule': scheduleSubmenu,
              '/alltime': alltimeSubmenu,
            };
            const submenu = submenuMap[item.path];
            const isExpanded = mobileExpandedMenu === item.path;

            // Updated mobile active logic
            const isActive =
              location.pathname === item.path ||
              (item.path === '/guides' && location.pathname.startsWith('/guides'));

            return !item.hasSubmenu ? (
              <MenuItem
                key={item.path}
                onClick={() => {
                  const isDisabled = (item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium);
                  if (!isDisabled) {
                    handleNavClick(item.path);
                  }
                }}
                disabled={(item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium)}
                sx={{
                  py: 2,
                  px: 2.5,
                  gap: 2.5,
                  bgcolor: isActive ? 'rgba(74,144,226,0.15)' : 'transparent',
                  borderLeft: isActive ? '4px solid #4a90e2' : '4px solid transparent',
                  opacity: (item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium) ? 0.5 : 1,
                }}
              >
                <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <IconWrapper>{item.icon}</IconWrapper>
                </Box>
                <Typography fontWeight={isActive ? 800 : 600} fontSize="0.95rem" color={isActive ? 'primary.main' : 'inherit'}>
                  {item.label}
                </Typography>
                {((item.requiresAuth && !isAuthenticated) || (item.requiresPremium && !isPremium)) && (
                  <Typography variant="caption" sx={{ ml: 'auto', color: 'text.secondary', fontSize: '0.7rem' }}>
                    Locked
                  </Typography>
                )}
              </MenuItem>
            ) : (
              <Box key={item.path}>
                <MenuItem
                  onClick={() => setMobileExpandedMenu(isExpanded ? null : item.path)}
                  sx={{
                    py: 2,
                    px: 2.5,
                    gap: 2.5,
                    fontWeight: 600,
                    bgcolor: isExpanded ? 'rgba(74,144,226,0.08)' : 'transparent',
                  }}
                >
                  <Box sx={{ width: 30, height: 30, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <IconWrapper>{item.icon}</IconWrapper>
                  </Box>
                  <Typography fontWeight={600} fontSize="0.95rem" sx={{ flex: 1 }}>
                    {item.label}
                  </Typography>
                  <ExpandMore
                    sx={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s',
                      fontSize: '1.5rem',
                    }}
                  />
                </MenuItem>
                <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                  <Box sx={{ bgcolor: 'rgba(0,0,0,0.02)' }}>
                    {submenu.map((sub, subIndex) => {
                      // Both Yahoo- and premium-gated tools stay navigable — the
                      // destination page renders the right gate.
                      const isSubActive = location.pathname === sub.path;
                      const prevSub = submenu[subIndex - 1];
                      const startsLeagueGroup =
                        sub.requiresAuth && (!prevSub || !prevSub.requiresAuth);

                      return (
                        <React.Fragment key={sub.path}>
                        {startsLeagueGroup && (
                          <Box
                            sx={{
                              pl: 7,
                              pr: 2.5,
                              pt: 1.5,
                              pb: 0.75,
                              borderTop: '1px solid #e8ebef',
                              fontSize: '0.62rem',
                              fontWeight: 700,
                              letterSpacing: 1.2,
                              textTransform: 'uppercase',
                              color: '#9aa4b2',
                            }}
                          >
                            Your Yahoo league
                          </Box>
                        )}
                        <MenuItem
                          onClick={() => handleNavClick(sub.path)}
                          sx={{
                            pl: 7,
                            pr: 2.5,
                            py: 2,
                            bgcolor: isSubActive ? 'rgba(74,144,226,0.15)' : 'transparent',
                            borderLeft: isSubActive ? '3px solid #4a90e2' : '3px solid transparent',
                            '&:hover': { bgcolor: 'rgba(74,144,226,0.08)' },
                          }}
                        >
                          <Typography fontSize="0.9rem" fontWeight={isSubActive ? 600 : 400} sx={{ flexGrow: 1 }}>
                            {sub.label}
                          </Typography>
                        </MenuItem>
                        </React.Fragment>
                      );
                    })}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Menu>
      </Box>

      {/* Profile Menu */}
      <Menu anchorEl={profileAnchorEl} open={Boolean(profileAnchorEl)} onClose={() => setProfileAnchorEl(null)}>
        <MenuItem disabled sx={{ opacity: 1 }}>
          <Box>
            <Typography fontWeight={700}>{isSignedIn ? (displayName || authUser?.email) : displayName}</Typography>
            {isSignedIn && authUser?.email && (
              <Typography variant="caption" color="text.secondary">{authUser.email}</Typography>
            )}
          </Box>
        </MenuItem>
        <MenuItem onClick={() => { setProfileAnchorEl(null); navigate('/profile'); }}>Profile</MenuItem>
        <MenuItem onClick={() => { setProfileAnchorEl(null); navigate('/pricing'); }}>
          <ConfirmationNumberOutlined sx={{ mr: 1, color: '#4a90e2' }} /> Passes
        </MenuItem>
        <MenuItem onClick={() => { setProfileAnchorEl(null); navigate('/about'); }}>About us</MenuItem>
        {!isAuthenticated && YAHOO_ENABLED && (
          <MenuItem onClick={() => { setProfileAnchorEl(null); handleYahooConnect(); }} disabled={yahooConnecting}>
            <SportsBasketballIcon sx={{ mr: 1, color: '#4a90e2' }} /> Connect Yahoo league
          </MenuItem>
        )}
        {isAuthenticated && (
          <MenuItem onClick={() => { logout(); setProfileAnchorEl(null); navigate('/matchup'); }} sx={{ color: '#d32f2f' }}>
            <Logout sx={{ mr: 1 }} /> Disconnect Yahoo
          </MenuItem>
        )}
        {isSignedIn && (
          <MenuItem onClick={async () => { await signOutAccount(); setProfileAnchorEl(null); navigate('/guides'); }} sx={{ color: '#d32f2f' }}>
            <Logout sx={{ mr: 1 }} /> Sign out
          </MenuItem>
        )}
      </Menu>

      <AuthModal open={authModalOpen} onClose={() => setAuthModalOpen(false)} />

      {/* CONTENT & FOOTER */}
      <Container maxWidth={false} disableGutters sx={{ flexGrow: 1 }}>
        {isPageLoading || (loadingLeagues && isAuthenticated) ? (
          <ReassuringLoader
            type={loadingLeagues && isAuthenticated ? 'leagues' : 'page'}
            minHeight="70vh"
          />
        ) : (
          <LeagueProvider
            selectedLeague={selectedLeague}
            onLeagueChange={setSelectedLeague}
            userLeagues={userLeagues}
            leagueTeams={leagueTeams}
            userTeamPlayers={userTeamPlayers}
            setLeagueTeams={setLeagueTeams}
            currentMatchup={currentMatchup}
            setCurrentMatchup={setCurrentMatchup}
            leagueSettings={leagueSettings}
            isLoadingLeagueData={isLoadingLeagueData}
          >
            {showDisplayAd && (
              <Container maxWidth="lg">
                <AdSlot slot={AD_SLOTS.siteDisplay} format="horizontal" minHeight={90} sx={{ my: 2 }} />
              </Container>
            )}
            {renderContent()}
            <SEOContent />
          </LeagueProvider>
        )}
      </Container>

      <Box
        component="footer"
        sx={{
          py: 2.5,
          textAlign: 'center',
          borderTop: '1px solid #f0f0f0',
          bgcolor: '#fafafa' // Slight off-white to distinguish from content
        }}
      >
        <Container maxWidth="md">
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 1, sm: 2 }}
            justifyContent="center"
            alignItems="center"
            divider={isMobile ? null : <Typography variant="caption" color="text.disabled">|</Typography>}
          >
            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              © {new Date().getFullYear()} Fantasy Goats Guru
            </Typography>

            <Link
              component={RouterLink}
              to="/privacy-policy"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' }
              }}
            >
              Privacy Policy
            </Link>

            <Link
              component={RouterLink}
              to="/terms"
              variant="caption"
              sx={{
                color: 'text.secondary',
                textDecoration: 'none',
                '&:hover': { color: 'primary.main', textDecoration: 'underline' }
              }}
            >
              Terms of Service
            </Link>
          </Stack>
        </Container>
      </Box>
    </Box>
  );
};

export default AlltimeLayout;
